import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { Axios } from '@helpers/ApiService';
import { useToast } from '@contexts/Toast';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import {
  errorMessage,
  onboardingStepId,
  responseTexts,
  storeSetupSubsteps,
} from '@helpers/Constants';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { ButcherHeader } from '@components/ButcherHeader';
import { CustomerOrderService } from '@services/CustomerService';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { City, State, PostalCode } from '@helpers/Models';
import { AxiosError } from 'axios';

export const AddressComponent: React.FC = () => {
  const { showToastMessage } = useToast();
  const navigate = useNavigate();
  const { updateOnboardingStep } = useLocalStorage();
  const [sameAsStore, setSameAsStore] = useState(false);
  const [storeZipCode, setStoreZipCode] = useState('');
  const [billingZipCode, setBillingZipCode] = useState('');
  const [isStepComplete, setIsStepComplete] = useState(false);
  const [hasExistingAddress, setHasExistingAddress] = useState(false);

  // State for zip code validation errors
  const [storeZipError, setStoreZipError] = useState<string>('');
  const [billingZipError, setBillingZipError] = useState<string>('');

  // State variables for address form fields
  const [storeAddress, setStoreAddress] = useState({
    address1: '',
    address2: '',
    address3: '',
  });

  const [billingAddress, setBillingAddress] = useState({
    address1: '',
    address2: '',
    address3: '',
  });

  // Single state variable for API response data
  const [storeAddressData, setStoreAddressData] = useState<{
    states: State[];
    cities: City[];
    postalCodes: PostalCode[];
  }>({
    states: [],
    cities: [],
    postalCodes: [],
  });

  const [billingAddressData, setBillingAddressData] = useState<{
    states: State[];
    cities: City[];
    postalCodes: PostalCode[];
  }>({
    states: [],
    cities: [],
    postalCodes: [],
  });

  // Fetch address data when component loads
  useEffect(() => {
    const fetchAddressData = async () => {
      try {
        console.log('Fetching address data...');
        const response = await Axios.get(ApiEndpoints.FETCH_ADDRESS);
        console.log('FETCH_ADDRESS response:', response.data);

        // Check if we have address data
        if (response.data?.data?.addresses) {
          const { storeAddress, billingAddress } = response.data.data.addresses;

          // Determine if there is existing data
          const hasStore = storeAddress && storeAddress.length > 0;
          const hasBilling = billingAddress && billingAddress.length > 0;
          setHasExistingAddress(hasStore || hasBilling);

          // Check if billing address is same as store address
          const isAddressSame =
            hasStore &&
            hasBilling &&
            storeAddress[0].address1 === billingAddress[0].address1 &&
            storeAddress[0].address2 === billingAddress[0].address2 &&
            storeAddress[0].address3 === billingAddress[0].address3 &&
            storeAddress[0].cityId === billingAddress[0].cityId &&
            storeAddress[0].stateId === billingAddress[0].stateId &&
            storeAddress[0].postalCodeId === billingAddress[0].postalCodeId;

          // Set the checkbox state
          setSameAsStore(isAddressSame);

          // Populate store address fields
          if (hasStore) {
            setStoreAddress({
              address1: storeAddress[0].address1 || '',
              address2: storeAddress[0].address2 || '',
              address3: storeAddress[0].address3 || '',
            });

            // Fetch postal code data for store address
            await fetchAddressDataByIds(
              storeAddress[0].cityId,
              storeAddress[0].stateId,
              storeAddress[0].postalCodeId,
              'store',
            );
          }

          // Populate billing address fields
          if (hasBilling) {
            setBillingAddress({
              address1: billingAddress[0].address1 || '',
              address2: billingAddress[0].address2 || '',
              address3: billingAddress[0].address3 || '',
            });

            // Only fetch billing address data if it's different from store
            if (!isAddressSame) {
              await fetchAddressDataByIds(
                billingAddress[0].cityId,
                billingAddress[0].stateId,
                billingAddress[0].postalCodeId,
                'billing',
              );
            }
          }
        }

        // Check if the address step is already complete
        const onboardingSteps = localStorage.getItem('onboardingSteps');
        if (onboardingSteps) {
          const steps = JSON.parse(onboardingSteps);
          const addressStep = steps.find(
            (step: any) => step.stepsId === onboardingStepId.ADDRESS,
          );
          setIsStepComplete(addressStep?.isComplete || false);
        }
      } catch (error) {
        console.error('Error fetching address data:', error);
      }
    };

    fetchAddressData();
  }, []);

  // Helper function to fetch address data by IDs (similar to handleZipCodeChange but using IDs)
  const fetchAddressDataByIds = async (
    cityId: number,
    stateId: number,
    postalCodeId: number,
    addressType: 'store' | 'billing',
  ) => {
    try {
      // Make API call with the IDs to get the full data including postal code
      const response = await Axios.post(ApiEndpoints.GET_STATE_CITY_POSTAL, {
        cityId,
        stateId,
        postalCodeId,
      });

      console.log('API Response for address data:', response.data);

      // Store the API response data in the appropriate state variable
      const { states, cities, postalCodes } = response.data.data;

      if (addressType === 'store') {
        setStoreAddressData({
          states,
          cities,
          postalCodes,
        });
        // Set the zip code from the fetched postal code
        if (postalCodes.length > 0) {
          setStoreZipCode(postalCodes[0].postalCode);
        }
        setStoreZipError(''); // clear any error
      } else {
        setBillingAddressData({
          states,
          cities,
          postalCodes,
        });
        // Set the zip code from the fetched postal code
        if (postalCodes.length > 0) {
          setBillingZipCode(postalCodes[0].postalCode);
        }
        setBillingZipError(''); // clear any error
      }
    } catch (error) {
      console.error('Error fetching address data by IDs:', error);
      // Handle error
      if (addressType === 'store') {
        setStoreZipError('Invalid postal code.');
        showToastMessage('Invalid postal code.', 'error');
      } else {
        setBillingZipError('Invalid postal code.');
        showToastMessage('Invalid postal code.', 'error');
      }
    }
  };

  const handleSameAsStore = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSameAsStore(e.target.checked);
    // If same as store is checked, copy store address to billing address
    if (e.target.checked) {
      setBillingAddress(storeAddress);
      setBillingZipCode(storeZipCode);
      setBillingAddressData(storeAddressData);
      setBillingZipError(storeZipError); // copy error state as well
    } else {
      // When unchecking, clear billing error
      setBillingZipError('');
    }
  };

  // Function to check if form is valid for submission
  const isFormValid = () => {
    // Validate Store Address (always required)
    const isStoreAddressValid =
      storeAddress.address1.trim() !== '' &&
      !!storeAddressData.cities[0]?.id &&
      !!storeAddressData.states[0]?.id &&
      !!storeAddressData.postalCodes[0]?.id &&
      storeZipError === ''; // no store zip error

    // Validate Billing Address
    let isBillingAddressValid = false;
    if (sameAsStore) {
      // If same as store is selected, store address validation is sufficient
      isBillingAddressValid = isStoreAddressValid;
    } else {
      // If same as store is not selected, validate billing address fields
      isBillingAddressValid =
        billingAddress.address1.trim() !== '' &&
        !!billingAddressData.cities[0]?.id &&
        !!billingAddressData.states[0]?.id &&
        !!billingAddressData.postalCodes[0]?.id &&
        billingZipError === ''; // no billing zip error
    }

    return isStoreAddressValid && isBillingAddressValid;
  };

  // Handle store address input changes
  const handleStoreAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setStoreAddress((prev) => ({
      ...prev,
      [name]: value,
    }));

    // If same as store is checked, also update billing address
    if (sameAsStore) {
      setBillingAddress((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Handle billing address input changes
  const handleBillingAddressChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = e.target;
    setBillingAddress((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Save handler – calls API, updates step completion, does NOT navigate
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid()) {
      showToastMessage('Please fill in all required fields.', 'error');
      return;
    }

    try {
      const payload = {
        storeAddress: {
          address1: storeAddress.address1,
          address2: storeAddress.address2,
          address3: storeAddress.address3,
          cityId: storeAddressData.cities[0]?.id || null,
          stateId: storeAddressData.states[0]?.id || null,
          postalCodeId: storeAddressData.postalCodes[0]?.id || null,
        },
        billingAddress: {
          address1: sameAsStore
            ? storeAddress.address1
            : billingAddress.address1,
          address2: sameAsStore
            ? storeAddress.address2
            : billingAddress.address2,
          address3: sameAsStore
            ? storeAddress.address3
            : billingAddress.address3,
          cityId: sameAsStore
            ? storeAddressData.cities[0]?.id
            : billingAddressData.cities[0]?.id || null,
          stateId: sameAsStore
            ? storeAddressData.states[0]?.id
            : billingAddressData.states[0]?.id || null,
          postalCodeId: sameAsStore
            ? storeAddressData.postalCodes[0]?.id
            : billingAddressData.postalCodes[0]?.id || null,
        },
        sameAsStore,
      };

      console.log('Submitting payload:', payload);

      const response = await Axios.post(ApiEndpoints.CREATE_ADDRESS, payload);
      // Update local storage with the newly changed address
      const butcherDetails = localStorage.getItem('butcherDetails');
      let details = butcherDetails ? JSON.parse(butcherDetails) : {};
      details = {
        ...details,
        billingAddress: response.data.data.butcherDetails.billingAddress,
        storeAddress: response.data.data.butcherDetails.storeAddress,
      };
      // Update the butcherDetails in local storage
      CustomerOrderService.setButcherDetails(details);

      // Show appropriate success message
      const successMsg = hasExistingAddress
        ? 'Address updated successfully.'
        : responseTexts.ADDRESS_ADD_SUCCESS;
      showToastMessage(successMsg, 'success');

      // Mark that we now have an address
      setHasExistingAddress(true);

      updateOnboardingStep(onboardingStepId.ADDRESS);
      setIsStepComplete(true); // enable the Next button
    } catch (error) {
      console.error('Error creating address:', error);
      showToastMessage(errorMessage.ERROR_CREATING_ADDRESS, 'error');
    }
  };

  // Next handler – only navigates, no API call
  const handleNext = () => {
    navigate(FrontendRoutes.TAX_DETAILS);
  };

  const handleZipCodeChange = async (
    value: string,
    addressType: 'store' | 'billing',
  ) => {
    // Only allow digits and limit to 5 characters
    const zipCode = value.replace(/\D/g, '').slice(0, 5);

    // Clear any previous error for this address type
    if (addressType === 'store') {
      setStoreZipError('');
    } else {
      setBillingZipError('');
    }

    if (addressType === 'store') {
      setStoreZipCode(zipCode);
      // If same as store is checked, also update billing zip code
      if (sameAsStore) {
        setBillingZipCode(zipCode);
      }
      // Clear address data when zip code changes (if less than 5)
      if (zipCode.length < 5) {
        setStoreAddressData({
          states: [],
          cities: [],
          postalCodes: [],
        });
        // If same as store is checked, also clear billing address data
        if (sameAsStore) {
          setBillingAddressData({
            states: [],
            cities: [],
            postalCodes: [],
          });
        }
      }
    } else {
      setBillingZipCode(zipCode);
      // Clear address data when zip code changes
      if (zipCode.length < 5) {
        setBillingAddressData({
          states: [],
          cities: [],
          postalCodes: [],
        });
      }
    }

    // Make API call when 5 digits are entered
    if (zipCode.length === 5) {
      try {
        const response = await Axios.post(ApiEndpoints.GET_STATE_CITY_POSTAL, {
          postalCode: zipCode,
        });

        console.log('API Response for postal code:', zipCode, response.data);

        // Store the API response data in the appropriate state variable
        const { states, cities, postalCodes } = response.data.data;

        // Clear any error for this address type
        if (addressType === 'store') {
          setStoreAddressData({
            states,
            cities,
            postalCodes,
          });
          setStoreZipError(''); // clear error
          // If same as store is checked, also update billing address data
          if (sameAsStore) {
            setBillingAddressData({
              states,
              cities,
              postalCodes,
            });
            setBillingZipError(''); // clear billing error as well
          }
        } else {
          setBillingAddressData({
            states,
            cities,
            postalCodes,
          });
          setBillingZipError(''); // clear error
        }

        // Optionally show a success toast (not required but can be added)
        // showToastMessage('Address details loaded.', 'success');
      } catch (error) {
        console.error('Error fetching address data:', error);
        let errorMsg = 'Error fetching address data';
        if (error instanceof AxiosError) {
          errorMsg = error.response?.data?.message || errorMsg;
        }

        // Set the error state and show the error toast
        if (addressType === 'store') {
          setStoreZipError(errorMsg);
          // Also clear any previously loaded address data
          setStoreAddressData({
            states: [],
            cities: [],
            postalCodes: [],
          });
          if (sameAsStore) {
            setBillingAddressData({
              states: [],
              cities: [],
              postalCodes: [],
            });
            setBillingZipError(errorMsg);
          }
        } else {
          setBillingZipError(errorMsg);
          setBillingAddressData({
            states: [],
            cities: [],
            postalCodes: [],
          });
        }

        // Show the error toast
        showToastMessage(errorMsg, 'error');
      }
    }
  };

  // Helper function to generate address labels
  const getAddressLabel = (field: string) => {
    if (field === 'address1') return 'Address 1 *';
    if (field === 'address2') return 'Address 2';
    if (field === 'address3') return 'Address 3';
    return 'Address';
  };

  const renderContent = (
    <>
      <div>
        {/* Breadcrumbs */}
        <Breadcrumbs
          title="Configure Settings"
          activeMainNav="Store"
          activeStep={2}
          subNavSteps={storeSetupSubsteps}
          fromSettings={false}
        />
      </div>
      <div className="w-11/12 pt-4 font-sans">
        <form className="space-y-8">
          <div className="bg-white rounded-lg">
            <h2 className="text-lg font-medium mb-4">Store Address</h2>
            <div className="space-y-2" data-testid="store-address">
              {['address1', 'address2', 'address3'].map((field) => (
                <div key={field}>
                  <label
                    htmlFor={`store-${field}`}
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {getAddressLabel(field)}
                  </label>
                  <input
                    type="text"
                    id={`store-${field}`}
                    name={field}
                    value={storeAddress[field as keyof typeof storeAddress]}
                    onChange={handleStoreAddressChange}
                    placeholder="XYZ"
                    className="mt-1 block w-full rounded-md border px-3 py-2 outline-gray-300 focus:outline-primary"
                  />
                </div>
              ))}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Postal Code Input Field - First */}
                <div>
                  <label
                    htmlFor="store-postal-input"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Zip Code *
                  </label>
                  <input
                    type="text"
                    id="store-postal-input"
                    name="postalCodeInput"
                    value={storeZipCode}
                    onChange={(e) =>
                      handleZipCodeChange(e.target.value, 'store')
                    }
                    placeholder="Enter 5-digit zip code"
                    maxLength={5}
                    className={`w-full px-3 py-2 border rounded-md text-sm outline-gray-320 focus:outline-primary h-[38px] ${storeZipError ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {storeZipError && (
                    <p className="text-red-500 text-xs mt-1">{storeZipError}</p>
                  )}
                </div>

                {/* State Input Field - Second */}
                <div className="relative">
                  <label
                    htmlFor="store-state"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    State *
                  </label>
                  <input
                    type="text"
                    id="store-state"
                    data-testid="store-state"
                    name="state"
                    value={storeAddressData.states[0]?.stateName || ''}
                    readOnly
                    placeholder="State will be filled automatically"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 h-[38px]"
                  />
                </div>

                {/* City Input Field - Third */}
                <div>
                  <label
                    htmlFor="store-city"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    City *
                  </label>
                  <input
                    type="text"
                    id="store-city"
                    name="city"
                    value={storeAddressData.cities[0]?.cityName || ''}
                    readOnly
                    placeholder="City will be filled automatically"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 h-[38px]"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg">
            <h2 className="text-lg font-medium mb-4">Billing Address</h2>
            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="same-as-store"
                checked={sameAsStore}
                onChange={handleSameAsStore}
                className="w-4 h-4 text-primary hover:cursor-pointer accent-primary focus:ring-primary border-gray-300 rounded"
              />
              <label
                htmlFor="same-as-store"
                className="ml-2 text-sm text-gray-700"
              >
                My Billing Address is same as the Store Address
              </label>
            </div>
            {!sameAsStore && (
              <div className="space-y-4" data-testid="billing-address">
                {['address1', 'address2', 'address3'].map((field) => (
                  <div key={field}>
                    <label
                      htmlFor={`billing-${field}`}
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      {getAddressLabel(field)}
                    </label>
                    <input
                      type="text"
                      id={`billing-${field}`}
                      name={field}
                      value={
                        billingAddress[field as keyof typeof billingAddress]
                      }
                      onChange={handleBillingAddressChange}
                      disabled={sameAsStore}
                      placeholder="XYZ"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                    />
                  </div>
                ))}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* Postal Code Input Field - First */}
                  <div>
                    <label
                      htmlFor="billing-postal-input"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Zip Code *
                    </label>
                    <input
                      type="text"
                      id="billing-postal-input"
                      name="postalCodeInput"
                      value={billingZipCode}
                      onChange={(e) =>
                        handleZipCodeChange(e.target.value, 'billing')
                      }
                      placeholder="Enter 5-digit zip code"
                      maxLength={5}
                      className={`w-full px-3 py-2 border rounded-md text-sm outline-gray-320 focus:outline-primary h-[38px] ${billingZipError ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {billingZipError && (
                      <p className="text-red-500 text-xs mt-1">
                        {billingZipError}
                      </p>
                    )}
                  </div>

                  {/* State Input Field - Second */}
                  <div className="relative">
                    <label
                      htmlFor="billing-state"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      State *
                    </label>
                    <input
                      type="text"
                      id="billing-state"
                      name="state"
                      value={billingAddressData.states[0]?.stateName || ''}
                      readOnly
                      placeholder="State will be filled automatically"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 h-[38px]"
                    />
                  </div>

                  {/* City Input Field - Third */}
                  <div>
                    <label
                      htmlFor="billing-city"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      City *
                    </label>
                    <input
                      type="text"
                      id="billing-city"
                      name="city"
                      value={billingAddressData.cities[0]?.cityName || ''}
                      readOnly
                      placeholder="City will be filled automatically"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 h-[38px]"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-between xl:justify-end lg:justify-end gap-4">
            {/* Save button – calls API, no navigation */}
            <button
              onClick={handleSave}
              type="button"
              disabled={!isFormValid()}
              className="w-[50%] xl:max-w-[15%] lg:max-w-[15%] px-6 py-1.5 border border-primary rounded-md text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save
            </button>

            {/* Next button – only navigation, enabled only if step is complete */}
            <button
              onClick={handleNext}
              type="button"
              disabled={!isStepComplete}
              className="w-[50%] xl:max-w-[15%] lg:max-w-[15%] px-6 py-1.5 border border-primary rounded-md text-sm font-medium text-white bg-primary hover:text-white focus:outline-none flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="text-inherit">Next</span>
              <FontAwesomeIcon
                icon={faArrowRight}
                className="text-inherit px-1 text-lg font-bold"
              />
            </button>
          </div>
        </form>
      </div>
    </>
  );

  return (
    <div className="w-screen h-screen overflow-auto flex justify-center items-start">
      <div className="w-11/12 p-4">
        <ButcherHeader isOnBoarding={true} />
        {renderContent}
      </div>
    </div>
  );
};